import { memo, useMemo, useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Music } from 'lucide-react';
import { useBandsQuery } from '@/hooks/useBandsQuery';
import { CardSkeleton } from '@/components/common/PageSkeleton';
import { getOptimizedImageUrl } from '@/utils/imageOptimization';

import { useScrollReveal } from '@/hooks/useScrollReveal';
import RevealBlock from '@/components/common/RevealBlock';

/* ── Premium Band Card ── */
interface PremiumBandCardProps {
  id: string;
  nome: string;
  cidade?: string;
  imagem_url?: string;
  slug?: string;
}

const PremiumBandCard = memo(({ id, nome, cidade, imagem_url, slug }: PremiumBandCardProps) => {
  return (
    <Link
      to={`/banda/${slug || id}`}
      className="premium-band-card group block flex-shrink-0 w-[280px] sm:w-[300px] md:w-[320px]"
    >
      <div className="overflow-hidden rounded-xl bg-white/[0.03] border border-white/[0.06]">
        {/* Imagem 4:3 */}
        <div className="relative overflow-hidden" style={{ paddingBottom: '75%' }}>
          {imagem_url ? (
            <img
              src={imagem_url}
              alt={nome}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a215a]/40 to-[#571a6d]/40" />
          )}
          {/* Light sweep */}
          <div className="band-sweep" />
        </div>

        {/* Info — always visible, below image */}
        <div className="p-4 bg-gradient-to-b from-[#0d1a3a] to-[#0a0e1f]">
          <h3 className="text-white font-bold text-base leading-tight mb-1 line-clamp-1">{nome}</h3>
          {cidade && (
            <div className="flex items-center gap-1.5 text-white/50 text-sm mb-3">
              <MapPin size={13} />
              <span>{cidade}</span>
            </div>
          )}
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-white/80 border border-white/10 rounded-lg px-3 py-1.5 bg-white/[0.04] group-hover:bg-white/[0.08] group-hover:border-white/20 transition-all duration-300">
            Saiba mais
            <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
});
PremiumBandCard.displayName = 'PremiumBandCard';

/* ── Featured Bands Section ── */
const FeaturedBands = memo(() => {
  const { bands, isLoading } = useBandsQuery();
  const { ref: sectionRef, visible: sectionVisible } = useScrollReveal(0.1);

  // Triplicate bands for seamless infinite loop
  const tripled = useMemo(() => {
    if (!bands || bands.length === 0) return [];
    return [...bands, ...bands, ...bands];
  }, [bands]);

  if (isLoading) {
    return (
      <section ref={sectionRef} className="py-20 md:py-28 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="h-12 w-64 mx-auto bg-white/5 animate-pulse rounded mb-4" />
            <div className="h-6 w-96 mx-auto bg-white/5 animate-pulse rounded" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => <CardSkeleton key={i} />)}
          </div>
        </div>
      </section>
    );
  }

  if (!bands || bands.length === 0) {
    return (
      <section ref={sectionRef} className="py-20 md:py-28 bg-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 tracking-tight">
            <span className="text-white">Bandas em </span><span className="sobre-gradient-text">Destaque</span>
          </h2>
          <p className="text-white/50">Nenhuma banda cadastrada ainda</p>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="py-20 md:py-28 sobre-mission-bg relative overflow-hidden">
      {/* Orbs decorativos */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="sobre-orb sobre-orb-1" />
        <div className="sobre-orb sobre-orb-3" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <RevealBlock>
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.25em] uppercase text-white/40 mb-4 font-medium">Conheça alguns artistas do sul</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
              <span className="text-white">Artistas em </span><span className="sobre-gradient-text">Destaque</span>
            </h2>
            <p className="text-lg text-white/50 max-w-2xl mx-auto">Conheça a essência dos artistas do sul</p>
          </div>
        </RevealBlock>
      </div>

      {/* Carrossel infinito — full width */}
      <RevealBlock delay={200}>
        <div className="premium-carousel-wrapper">
          <div
            className="premium-band-carousel"
            style={{
              '--band-count': bands.length,
            } as React.CSSProperties}
          >
            {tripled.map((band, index) => (
              <PremiumBandCard
                key={`${band.id}-${index}`}
                id={band.id}
                nome={band.nome}
                cidade={band.cidade}
                imagem_url={band.imagem_url ? getOptimizedImageUrl(band.imagem_url, { width: 600, height: 450, quality: 80 }) : undefined}
                slug={band.slug}
              />
            ))}
          </div>
        </div>
      </RevealBlock>

      <div className="container mx-auto px-4 relative z-10">
        <RevealBlock delay={350}>
          <div className="text-center mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/bandas">
              <button className="sobre-cta-button text-white group px-8 py-4 rounded-xl font-medium text-base inline-flex items-center gap-3 hover:opacity-90 transition-all duration-300 hover:scale-[1.02]">
                Ver todas as bandas
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
            <Link to="/cadastre-sua-banda">
              <button className="sobre-cta-button text-white group px-8 py-4 rounded-xl font-medium text-base inline-flex items-center gap-3 hover:opacity-90 transition-all duration-300 hover:scale-[1.02]">
                Cadastre sua banda
                <Music size={18} className="group-hover:scale-110 transition-transform" />
              </button>
            </Link>
          </div>
        </RevealBlock>
      </div>
    </section>
  );
});

FeaturedBands.displayName = 'FeaturedBands';
export default FeaturedBands;
